import { lazy } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// react-router
import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "../../../hooks/useLocalizedNavigate";

import PointieBox from "../../components/PointieBox";
import PointieTypography from "../../components/PointieTypography";
import PointieButton from "../../components/PointieButton";

import DefaultNavbar from "../../DefaultNavbar";
import DefaultFooter from "../../DefaultFooter";

// Above-the-fold + crawl-critical sections — eager. HowItWorks ships
// HowTo JSON-LD via Helmet so it must render in the initial pass.
import Counters from "./sections/Counters";
import HowItWorks from "./sections/HowItWorks";
import FeatureHighlights from "./sections/FeatureHighlights";

import LazySection from "../../components/LazySection";

// Routes
import routes from "../../../routes";
import footerRoutes from "../../../app/footer.routes";

// Images
import logoCT from "../../../assets/images/pointie_logo.png";

import {
  downloadPointieData,
  checkOffersData,
  getDownloadUrl,
} from "../../../data/DownloadButtons";
import { trackEvent } from "../../../libraries/firebase";
import SEO from "../../components/SEO";
import heroGradients from "../../../assets/theme/heroGradients";

// Below-the-fold sections — code-split + scroll-triggered via LazySection.
// Bundle size win is meaningful (LatestBlogPosts pulls in 33KB blog metadata,
// FeatureShowcase pulls Dialog + screenshots, ProviderShowcase pulls a
// referrals JSON). Crawl-critical copy for these (provider names, blog
// titles, CTA text) is duplicated in static llms.txt / index.html.md and
// dedicated pages, so deferred mount is SEO-safe.
const FeatureShowcase = lazy(() => import("./sections/FeatureShowcase"));
const ProviderShowcase = lazy(() => import("./sections/ProviderShowcase"));
const LatestBlogPosts = lazy(() => import("./sections/LatestBlogPosts"));
const Download = lazy(() => import("./sections/Download"));

function Home() {
  const { t } = useTranslation(["home", "common"]);
  const lp = useLocalizedPath();
  return (
    <>
      <SEO title={t("home:seo.title")} description={t("home:seo.description")} path="/" />
      <DefaultNavbar routes={routes} actions={[checkOffersData, downloadPointieData]} sticky />
      <PointieBox
        minHeight={{ xs: "55vh", md: "55vh" }}
        width="100%"
        sx={{
          backgroundImage: heroGradients.main,
          backgroundSize: "cover",
          backgroundPosition: "top",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            mt={{ xs: 16, lg: 0 }}
            mb={{ xs: 8, lg: 0 }}
            justifyContent="center"
            mx="auto"
            textAlign="center"
          >
            <PointieBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                width: "100%",
              }}
            >
              <img
                src={logoCT}
                width="44"
                height="44"
                style={{ width: 44, marginRight: 12 }}
                alt="Pointie logo"
              />
              <PointieTypography
                variant="h1"
                color="white"
                sx={{
                  textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                  fontSize: { xs: "2rem", md: "3rem" },
                }}
              >
                {t("home:hero.title")}
              </PointieTypography>
            </PointieBox>
            <PointieTypography
              variant="h2"
              color="white"
              fontWeight="bold"
              mb={2}
              sx={{
                textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                fontSize: { xs: "1.4rem", md: "2rem" },
                width: "100%",
              }}
            >
              {t("home:hero.subtitle")}
            </PointieTypography>
            <PointieTypography
              variant="body1"
              color="white"
              opacity={0.9}
              mb={4}
              px={{ xs: 2, md: 8 }}
              sx={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)", width: "100%" }}
            >
              {t("home:hero.descriptionLine1")}
              <br />
              {t("home:hero.descriptionLine2")}
            </PointieTypography>
            <PointieBox
              sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center", mb: 4 }}
            >
              <PointieButton
                component={Link}
                to={lp("/offers/on-the-fly")}
                variant="contained"
                color="white"
                size="large"
                sx={{ fontWeight: 700, color: "#1a73e8" }}
                onClick={() => trackEvent("click_check_offers", { location: "hero" })}
              >
                <Icon sx={{ mr: 0.5 }}>search</Icon>
                {t("common:buttons.checkOffers")}
              </PointieButton>
              <PointieButton
                component="a"
                href={getDownloadUrl()}
                target="_blank"
                rel="noreferrer"
                variant="gradient"
                color="info"
                size="large"
                sx={{ fontWeight: 700 }}
                onClick={() => trackEvent("click_download", { location: "hero" })}
              >
                <Icon sx={{ mr: 0.5 }}>download</Icon>
                {t("common:buttons.downloadFree")}
              </PointieButton>
            </PointieBox>
          </Grid>
        </Container>
      </PointieBox>
      <Card
        component="main"
        sx={{
          p: { xs: 2, md: 4 },
          mx: { xs: 1, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.95),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* Mobile CTA — nudge users to Check Offers */}
        <PointieBox
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            py: 2.5,
            px: 2,
            mb: 2,
            borderRadius: 2,
            background: "linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)",
          }}
        >
          <PointieTypography variant="h6" component="h3" fontWeight="bold" mb={0.5}>
            {t("home:mobileCta.title")}
          </PointieTypography>
          <PointieTypography variant="body2" color="text" mb={1.5} sx={{ lineHeight: 1.3 }}>
            {t("home:mobileCta.description")}
          </PointieTypography>
          <PointieButton
            component={Link}
            to={lp("/offers/on-the-fly")}
            variant="gradient"
            color="info"
            size="small"
            sx={{ fontWeight: 700 }}
            onClick={() => trackEvent("click_check_offers", { location: "mobile_cta" })}
          >
            <Icon sx={{ mr: 0.5 }}>search</Icon>
            {t("common:buttons.checkOffersNow")}
          </PointieButton>
        </PointieBox>
        <Counters />
        <HowItWorks />
        <FeatureHighlights />
        <LazySection Component={FeatureShowcase} minHeight={{ xs: 900, md: 620 }} />
        <LazySection Component={ProviderShowcase} minHeight={{ xs: 700, md: 420 }} />
        <LazySection Component={LatestBlogPosts} minHeight={{ xs: 900, md: 520 }} />
        <LazySection Component={Download} minHeight={{ xs: 360, md: 280 }} />
      </Card>
      <PointieBox pt={3} px={1} mt={3}>
        <DefaultFooter content={footerRoutes} />
      </PointieBox>
    </>
  );
}

export default Home;
